import { Button } from "@/components/button";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/chart";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { CPAClientInfo, CPAOrgData } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, XAxis, YAxis } from "recharts";

const CPA_GOAL = 100;
const MAX_WEEKS = 6;
const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--chart-6))",
  "hsl(var(--chart-7))",
  "hsl(var(--chart-8))",
];

type CPAChartProps = {
  orgs: CPAOrgData[];
  allClients: CPAClientInfo[];
  selectedOrgIds: number[];
  onSelectionChange: (orgIds: number[]) => void;
  isLoadingClients: boolean;
  isLoadingData: boolean;
};

function buildChartData(orgs: CPAOrgData[]) {
  const maxWeek = Math.min(
    orgs.reduce((max, org) => {
      const orgMax = org.weeks.reduce((m, w) => Math.max(m, w.week_number), 0);
      return Math.max(max, orgMax);
    }, 0),
    MAX_WEEKS,
  );

  return Array.from({ length: maxWeek + 1 }, (_, weekNum) => {
    const point: Record<string, number | string> = { week: weekNum };
    for (const org of orgs) {
      const week = org.weeks.find(w => w.week_number === weekNum);
      if (week) {
        point[String(org.org_id)] = parseFloat((week.cpa_achievement * 100).toFixed(1));
      }
    }
    return point;
  });
}

function buildChartConfig(orgs: CPAOrgData[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < orgs.length; i++) {
    config[String(orgs[i].org_id)] = {
      label: orgs[i].org_name || String(orgs[i].org_id),
      color: CHART_COLORS[i % CHART_COLORS.length],
    };
  }
  return config;
}

export function CPAChart({
  orgs,
  allClients,
  selectedOrgIds,
  onSelectionChange,
  isLoadingClients,
  isLoadingData,
}: CPAChartProps) {
  const [open, setOpen] = useState(false);
  const chartData = buildChartData(orgs);
  const chartConfig = buildChartConfig(orgs);

  const toggleOrg = (orgId: number) => {
    if (selectedOrgIds.includes(orgId)) {
      onSelectionChange(selectedOrgIds.filter(id => id !== orgId));
    } else {
      onSelectionChange([...selectedOrgIds, orgId]);
    }
  };

  const buttonLabel = isLoadingClients
    ? "Loading clients..."
    : selectedOrgIds.length === 0
      ? "Select clients..."
      : `${selectedOrgIds.length} client${selectedOrgIds.length !== 1 ? "s" : ""} selected`;

  return (
    <div className="flex flex-col gap-4">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoadingClients}
            className="w-[320px] justify-between"
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronsUpDown className="opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search clients..." className="h-9" />
            <CommandList>
              <CommandEmpty>No clients found.</CommandEmpty>
              <CommandGroup>
                {allClients.map(client => (
                  <CommandItem
                    key={client.org_id}
                    value={String(client.org_id)}
                    keywords={[client.org_name]}
                    onSelect={() => toggleOrg(client.org_id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedOrgIds.includes(client.org_id) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{client.org_name || String(client.org_id)}</span>
                    <span className="ml-auto text-xs text-muted-foreground shrink-0">{client.first_campaign_date}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {isLoadingData ? (
        <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
          Loading CPA data...
        </div>
      ) : orgs.length === 0 ? (
        <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
          Select clients above to view CPA achievement.
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="min-h-[200px]">
          <LineChart data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="week" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={v => `Wk ${v}`} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} tickFormatter={v => `${v}%`} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={v => `Wk ${v}`}
                  formatter={(value, name, item) => {
                    const pct = Number(value);
                    const onTarget = pct < CPA_GOAL;
                    return (
                      <>
                        <div className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
                        <span className="text-muted-foreground truncate max-w-[120px]">
                          {chartConfig[name]?.label ?? name}
                        </span>
                        <span className="ml-auto font-mono font-medium tabular-nums">{pct}%</span>
                        {onTarget ? (
                          <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0" />
                        )}
                      </>
                    );
                  }}
                />
              }
            />
            <ReferenceArea y1={0} y2={CPA_GOAL} fill="hsl(var(--chart-2))" fillOpacity={0.15} />
            <ReferenceLine
              y={CPA_GOAL}
              stroke="hsl(var(--chart-2))"
              strokeDasharray="4 4"
              label={{ value: "Goal: 100%", position: "insideTopRight", fill: "hsl(var(--chart-2))", fontSize: 12 }}
            />
            <ReferenceLine
              x={4}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="4 4"
              label={{ value: "Wk 4", position: "insideTopRight", fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            />
            {orgs.map((org, i) => (
              <Line
                key={org.org_id}
                dataKey={String(org.org_id)}
                type="monotone"
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ChartContainer>
      )}
    </div>
  );
}
